import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Typography,
  Divider,
  LinearProgress,
  Button,
  Stack,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getSubscribedPlansRequest } from "../../redux/DepositePlan/depositeSlice";
import { useAppTheme } from "../../theme/useAppTheme";

const BullionDepositeHistory = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { subscribedPlans, loading } = useSelector((state) => state.deposit);

  const themeColors = useAppTheme();

  useEffect(() => {
    dispatch(getSubscribedPlansRequest());
  }, [dispatch]);
  const [statusFilter, setStatusFilter] = useState("ALL");

  const calculateCompletedDays = (startDate, durationDays) => {
    const currentDate = new Date();
    const start = new Date(startDate);
    let completedDays = Math.floor(
      (currentDate - start) / (1000 * 60 * 60 * 24),
    );
    completedDays = Math.max(1, Math.min(completedDays, durationDays));
    return completedDays;
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: "center", mt: 10 }}>
        <Typography variant="h6">Loading subscribed plans...</Typography>
      </Box>
    );
  }

  if (!subscribedPlans || subscribedPlans.length === 0) {
    return (
      <Box sx={{ textAlign: "center", mt: 10 }}>
        <Typography variant="h6">No data found</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        px: 2,
        py: 4,
        background: themeColors.scaffoldGradientColors,
      }}
    >
      <Card
        sx={{
          maxWidth: 1200,
          width: "100%",
          borderRadius: 3,
          boxShadow: 8,
          background: themeColors.scaffoldGradientColors,
          color: themeColors.text,
          border: `1px solid ${themeColors.scaffold}`,
        }}
      >
        <Box sx={{ px: { xs: 3, sm: 5 }, py: 4 }}>
          {/* Page Title */}
          <Typography
            variant="h4"
            fontWeight="bold"
            align="center"
            sx={{
              mb: 3,
              fontSize: { xs: "1.8rem", sm: "2.2rem" },
              background: themeColors.primary,
              backgroundClip: "text",
              textFillColor: "transparent",
            }}
          >
            Bullion Deposit History
          </Typography>

          <Divider sx={{ background: themeColors.primary, mb: 3 }} />

          {/* Status Filters */}
          <Stack
            direction="row"
            spacing={2}
            justifyContent="center"
            sx={{ mb: 3 }}
          >
            {["ALL", "RUNNING", "COMPLETED", "STOPPED", "DISCONTINUED"].map(
              (status) => (
                <Button
                  key={status}
                  variant={statusFilter === status ? "contained" : "outlined"}
                  onClick={() => setStatusFilter(status)}
                  sx={{
                    textTransform: "capitalize",
                    background: themeColors.primary,
                    color: themeColors.text,
                    "&:hover": {
                      background: themeColors.svgColor,
                    },
                    border: `1px solid ${themeColors.scaffold}`,
                  }}
                >
                  {status.toLowerCase()}
                </Button>
              ),
            )}
          </Stack>

          {subscribedPlans
            .filter((plan) => {
              if (statusFilter === "ALL") return true;

              // map CUSTOMERDISCONTINUED → STOPPED
              if (statusFilter === "STOPPED") {
                return plan.status === "CUSTOMERDISCONTINUED";
              }

              return plan.status === statusFilter;
            })
            .map((plan, index) => {
              const durationDays = plan.depositplan.duration;
              const completedDays = calculateCompletedDays(
                plan.startDate,
                durationDays,
              );
              const progress = (completedDays / durationDays) * 100;

              const benefit = (
                (((plan.depositplan.qty * plan.depositplan.interest) / 100) / 365) *
                durationDays
              ).toFixed(4);

              return (
                <Card
                  key={index}
                  sx={{
                    mb: 3,
                    p: 2,
                    borderRadius: 3,
                    boxShadow: 3,
                    border: `1px solid ${themeColors.scaffold}`,
                    color: themeColors.text,
                    background: themeColors.bigContainer,
                  }}
                >
                  <Stack spacing={2}>
                    <Typography variant="h6" sx={{ color: themeColors.text }}>
                      {plan.depositplan.name}
                    </Typography>

                    {/* Metal info */}
                    <Box
                      sx={{
                        background:
                          plan.bullion.metalName === "Gold"
                            ? "linear-gradient(135deg, #FFD700, #DAA520)"
                            : plan.bullion.metalName === "Silver"
                              ? "linear-gradient(135deg, #C0C0C0, #A9A9A9)"
                              : "linear-gradient(135deg, #E5E4E2, #D3D3D3)",
                        borderRadius: 2,
                        p: 1,
                        textAlign: "center",
                        border: `1px solid ${themeColors.scaffold}`,
                        color: "#ffffff",
                      }}
                    >
                      <Typography>{plan.bullion.metalName}</Typography>
                      <Typography>
                        {plan.bullion.karat}k-{plan.bullion.purity}
                      </Typography>
                    </Box>

                    {/* Weight */}
                    <Typography align="center" variant="body1">
                      {plan.weight} gm(s) (Deposit)
                    </Typography>

                    {/* Progress */}
                    <LinearProgress
                      variant="determinate"
                      value={progress}
                      sx={{ height: 10, borderRadius: 5 }}
                    />

                    {/* Dates */}
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      sx={{ fontSize: 12 }}
                    >
                      <Typography>
                        {new Date(plan.startDate).toLocaleDateString()}
                      </Typography>
                      <Typography>
                        {completedDays} days / {durationDays} days
                      </Typography>
                      <Typography>
                        {new Date(
                          plan.startDate + durationDays * 24 * 60 * 60 * 1000,
                        ).toLocaleDateString()}
                      </Typography>
                    </Stack>

                    {/* Benefit */}
                    <Typography align="center" variant="body2">
                      Upon completion of this plan's duration, you will receive
                      a benefit of <strong>{benefit}</strong> gm(s) for every{" "}
                      {plan.depositplan.qty} gm(s) deposited
                    </Typography>

                    {/* View Details */}
                    <Box textAlign="center">
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: "none",
                          background: themeColors.primary,
                          color: themeColors.text,
                          "&:hover": {
                            background: themeColors.svgColor,
                          },
                          border: `1px solid ${themeColors.scaffold}`,
                        }}
                        onClick={() =>
                          navigate("/depositDetails", { state: { plan } })
                        }
                      >
                        View Details
                      </Button>
                    </Box>
                  </Stack>
                </Card>
              );
            })}
        </Box>
      </Card>
    </Box>
  );
};

export default BullionDepositeHistory;
